import { isGuideChatProduct, type CollectedQuoteData, type GuideChatProduct, type HistoryTurn } from './types';

export const SESSION_STORAGE_KEY = 'rm_guide_chat';

// Intent to prefill the loan form, kept apart from the widget conversation so a chat never prefills a form on its own.
export interface GuideChatPrefillIntent {
  conversationId: string;
  collected: CollectedQuoteData;
  trackedQuoteTokens: string[];
}

export interface GuideChatSession {
  conversationId: string | null;
  // Signed by the backend for conversationId: without it the backend refuses to resume the conversation.
  conversationToken?: string | null;
  history: HistoryTurn[];
  collected: CollectedQuoteData;
  dismissedOpeners: string[];
  openerShownOnPath: string[];
  prefill?: GuideChatPrefillIntent | null;
  // The backend closed the conversation: a reload must not reopen the input.
  closed?: boolean;
  // The product of the last answer, so a conversation carried to another page keeps its corpus.
  product?: GuideChatProduct | null;
  // The product of the page the widget last ran on: moving to another product's pages resets the conversation product.
  pageProduct?: GuideChatProduct | null;
}

export function emptySession(): GuideChatSession {
  return { conversationId: null, conversationToken: null, history: [], collected: {}, dismissedOpeners: [], openerShownOnPath: [], closed: false, product: null, pageProduct: null };
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function sanitize(raw: unknown): GuideChatSession {
  const base = emptySession();
  if (!isRecord(raw)) return base;
  const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  const prefill = isRecord(raw.prefill) && typeof raw.prefill.conversationId === 'string'
    ? {
        conversationId: raw.prefill.conversationId,
        collected: isRecord(raw.prefill.collected) ? (raw.prefill.collected as CollectedQuoteData) : {},
        trackedQuoteTokens: strings(raw.prefill.trackedQuoteTokens),
      }
    : null;
  return {
    conversationId: typeof raw.conversationId === 'string' ? raw.conversationId : null,
    conversationToken: typeof raw.conversationToken === 'string' ? raw.conversationToken : null,
    history: Array.isArray(raw.history)
      ? raw.history
          .filter((t): t is HistoryTurn => isRecord(t) && typeof t.question === 'string' && typeof t.answer === 'string')
          .map((t) => ({
            question: t.question,
            answer: t.answer,
            ...(Array.isArray(t.citations)
              ? { citations: t.citations.filter((c) => isRecord(c) && typeof c.url === 'string') }
              : {}),
          }))
      : [],
    collected: isRecord(raw.collected) ? (raw.collected as CollectedQuoteData) : {},
    dismissedOpeners: strings(raw.dismissedOpeners),
    openerShownOnPath: strings(raw.openerShownOnPath),
    prefill,
    closed: raw.closed === true,
    product: isGuideChatProduct(raw.product) ? raw.product : null,
    pageProduct: isGuideChatProduct(raw.pageProduct) ? raw.pageProduct : null,
  };
}

export function loadSession(): GuideChatSession {
  const raw = storage()?.getItem(SESSION_STORAGE_KEY);
  if (!raw) return emptySession();
  try {
    return sanitize(JSON.parse(raw));
  } catch {
    return emptySession();
  }
}

export function saveSession(patch: Partial<GuideChatSession>): GuideChatSession {
  const next = { ...loadSession(), ...patch };
  try {
    storage()?.setItem(SESSION_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Quota or private mode: the chat still works for the current page.
  }
  return next;
}

export function conversationTokenFor(conversationId: unknown): string | null {
  if (typeof conversationId !== 'string' || !conversationId) return null;
  const session = loadSession();
  return session.conversationId === conversationId ? (session.conversationToken ?? null) : null;
}

// A page or wizard step already shown or dismissed gets no second opener; there is no cap across pages.
export function claimOpenerSlot(key: string): boolean {
  const session = loadSession();
  if (session.openerShownOnPath.includes(key) || session.dismissedOpeners.includes(key)) return false;
  saveSession({ openerShownOnPath: [...session.openerShownOnPath, key] });
  return true;
}

export function markOpenerDismissed(key: string): void {
  const session = loadSession();
  if (!session.dismissedOpeners.includes(key)) saveSession({ dismissedOpeners: [...session.dismissedOpeners, key] });
}

export function clearSession(): void {
  try {
    storage()?.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}
